import React, { useState, useMemo, useCallback, version, cloneElement } from 'react';
import { FocusOn } from 'react-focus-on';
import { v4 } from 'uuid';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import 'tslib';
import { noop } from '../../utils/noop.esm.js';
import { useOptionallyControlledState } from '../../utils/useOptionallyControlledState.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { POPOVER_PLACEMENTS } from '../../utils/constants.esm.js';
import { useShouldRender } from '../../utils/useShouldRender.esm.js';
import { Card } from '../Card/Card.esm.js';
import '../Icon/ImgIcon.esm.js';
import '../Icon/FaIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Tooltip/Tooltip.esm.js';
import { Portal } from '../Portal/Portal.esm.js';

/**
 * Renders its children in a focused mode without a backdrop.
 *
 * When the Popover is open, all other actions on the page are enabled but focus is trapped within the Popover.
 *
 * Set the initial focus within in the Popover with one of two attributes:
 *
 *   - `data-autofocus` - the specific element will get focus on mount
 *   - `data-autofocus-inside` - the first interactive child will get focus on mount
 *
 * The escape key, and clicking outside the dialog will close the dialog.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Popover, CardBody, CardHeader, CardFooter } from "@panwds/react-ui";
 * ```
 */
var Popover = function (_a) {
    var _b = _a.id, id = _b === void 0 ? undefined : _b, _c = _a.setPopoverState, setPopoverState = _c === void 0 ? undefined : _c, button = _a.button, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, _d = _a.defaultPlacement, defaultPlacement = _d === void 0 ? POPOVER_PLACEMENTS.BOTTOM_END : _d, children = _a.children, _e = _a.defaultIsOpen, defaultIsOpen = _e === void 0 ? false : _e, userIsOpen = _a.isOpen, label = _a.label, _f = _a.onIsOpenChange, onIsOpenChange = _f === void 0 ? noop : _f, _g = _a.showLabel, showLabel = _g === void 0 ? true : _g, shards = _a.shards;
    var _h = useState(defaultPlacement), placement = _h[0], setPlacement = _h[1];
    var _j = useState(0), refWidth = _j[0], setRefWidth = _j[1];
    var _k = useState(0), refHeight = _k[0], setRefHeight = _k[1];
    //represents the pixels from the edge of the popover to the tip of the caret
    var caretPopoverOffset = 33;
    var _l = usePopper({
        placement: defaultPlacement,
        strategy: "fixed",
        modifiers: [
            {
                name: "offset",
                options: {
                    offset: function () {
                        if (placement === POPOVER_PLACEMENTS.BOTTOM_START ||
                            placement === POPOVER_PLACEMENTS.TOP_START) {
                            return [refWidth / 2 - caretPopoverOffset, 0];
                        }
                        else if (placement === POPOVER_PLACEMENTS.BOTTOM_END ||
                            placement === POPOVER_PLACEMENTS.TOP_END) {
                            return [-(refWidth / 2 - caretPopoverOffset), 0];
                        }
                        else if (placement === POPOVER_PLACEMENTS.LEFT_START ||
                            placement === POPOVER_PLACEMENTS.RIGHT_START) {
                            return [refHeight / 2 - caretPopoverOffset, 0];
                        }
                        else {
                            return [-(refHeight / 2 - caretPopoverOffset), 0];
                        }
                    }
                }
            },
            {
                name: "getPlacement",
                enabled: true,
                phase: "afterWrite",
                fn: function (_a) {
                    var state = _a.state;
                    setRefHeight(state.rects.reference.height);
                    setRefWidth(state.rects.reference.width);
                    setPlacement(state.placement);
                }
            },
            {
                name: "flip",
                options: {
                    fallbackPlacements: [
                        POPOVER_PLACEMENTS.BOTTOM_START,
                        POPOVER_PLACEMENTS.BOTTOM_END,
                        POPOVER_PLACEMENTS.TOP_START,
                        POPOVER_PLACEMENTS.TOP_END,
                        POPOVER_PLACEMENTS.LEFT_START,
                        POPOVER_PLACEMENTS.LEFT_END,
                        POPOVER_PLACEMENTS.RIGHT_START,
                        POPOVER_PLACEMENTS.RIGHT_END,
                    ]
                }
            },
        ]
    }), triggerRef = _l.triggerRef, containerRef = _l.containerRef;
    var _m = useOptionallyControlledState({
        currentValue: userIsOpen,
        defaultValue: defaultIsOpen,
        onChange: onIsOpenChange
    }), isOpen = _m[0], setIsOpen = _m[1];
    var shouldRender = useShouldRender(isOpen);
    var labelId = useMemo(function () { return "popover-label-".concat(v4()); }, []);
    var buttonId = useMemo(function () { return "popover-button-".concat(v4()); }, []);
    var togglePopover = useCallback(function (e) {
        // Between react 16 and 17 event propogation changed such that 17 and after we need to stop propogation of this event,
        // while for 16 we need to let the event propogate.
        if (!/^16/.test(version)) {
            e.stopPropagation();
        }
        setIsOpen(!isOpen);
    }, [isOpen, setIsOpen]);
    var closePopover = useCallback(function () {
        setPopoverState ? setPopoverState({ isOpen: false }) : noop;
        setIsOpen(false);
    }, [setIsOpen]);
    var handleKeyDown = useCallback(function (e) {
        switch (e.key) {
            case " ":
            case "Enter":
                setPopoverState ? setPopoverState({ id: id, isOpen: !isOpen }) : noop;
                setIsOpen(!isOpen);
                e.preventDefault();
        }
    }, [setIsOpen]);
    var opacity0 = !shouldRender || !isOpen;
    var cardClassName = classNames(opacity0 ? "tw-opacity-0" : "tw-opacity-100", "tw-transition-opacity tw-ease-linear tw-duration-200");
    var defaultStyle = "tw-relative tw-z-20 before:tw-content-[''] before:tw-absolute before:tw-h-0 before:tw-w-0 before:tw-border-solid before:tw-border-transparent before:tw-border-8 after:tw-content-[''] after:tw-absolute after:tw-h-0 after:tw-w-0 after:tw-border-solid after:tw-border-transparent after:tw-border-8";
    if (placement === POPOVER_PLACEMENTS.BOTTOM_END) {
        defaultStyle = classNames(defaultStyle, "tw-top-3 before:tw-right-6 before:tw--top-2 before:tw-border-t-0 before:tw-border-b-gray-300 dark:before:tw-border-b-gray-700  after:tw-right-6 after:tw-border-t-0 after:tw--top-1.5 after:tw-border-b-white dark:after:tw-border-b-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM_START) {
        defaultStyle = classNames(defaultStyle, "tw-top-3 before:tw-left-6 before:tw--top-2 before:tw-border-t-0 before:tw-border-b-gray-300 dark:before:tw-border-b-gray-700 after:tw-left-6 after:tw-border-t-0 after:tw--top-1.5 after:tw-border-b-white dark:after:tw-border-b-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_START) {
        defaultStyle = classNames(defaultStyle, "tw-bottom-3 before:tw-left-6 before:tw--bottom-2 before:tw-border-b-0 before:tw-border-t-gray-300 dark:before:tw-border-t-gray-700 after:tw-left-6 after:tw-border-b-0 after:tw--bottom-1.5 after:tw-border-t-white dark:after:tw-border-t-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_END) {
        defaultStyle = classNames(defaultStyle, "tw-bottom-3 before:tw-right-6 before:tw--bottom-2 before:tw-border-b-0 before:tw-border-t-gray-300 dark:before:tw-border-t-gray-700 after:tw-right-6 after:tw-border-b-0 after:tw--bottom-1.5 after:tw-border-t-white dark:after:tw-border-t-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_START) {
        defaultStyle = classNames(defaultStyle, "tw-right-3 before:tw-top-6 before:tw--right-2 before:tw-border-r-0 before:tw-border-l-gray-300 dark:before:tw-border-l-gray-700 after:tw-top-6 after:tw-border-r-0 after:tw--right-1.5 after:tw-border-l-white dark:after:tw-border-l-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_END) {
        defaultStyle = classNames(defaultStyle, "tw-right-3 before:tw-bottom-6 before:tw--right-2 before:tw-border-r-0 before:tw-border-l-gray-300 dark:before:tw-border-l-gray-700 after:tw-bottom-6 after:tw-border-r-0 after:tw--right-1.5 after:tw-border-l-white dark:after:tw-border-l-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_START) {
        defaultStyle = classNames(defaultStyle, "tw-left-3 before:tw-top-6 before:tw--left-2 before:tw-border-l-0 before:tw-border-r-gray-300 dark:before:tw-border-r-gray-700 after:tw-top-6 after:tw-border-l-0 after:tw--left-1.5 after:tw-border-r-white dark:after:tw-border-r-gray-1000");
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_END) {
        defaultStyle = classNames(defaultStyle, "tw-left-3 before:tw-bottom-6 before:tw--left-2 before:tw-border-l-0 before:tw-border-r-gray-300 dark:before:tw-border-r-gray-700 after:tw-bottom-6 after:tw-border-l-0 after:tw--left-1.5 after:tw-border-r-white dark:after:tw-border-r-gray-1000");
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { "data-metrics": dataMetrics, "data-testid": dataTestId || "popover", className: "tw-font-lato tw-flex ".concat(showLabel && "tw-space-x-2") },
            React.createElement("label", { id: labelId, htmlFor: buttonId, className: classNames("tw-leading-loose tw-text-sm", !showLabel && "tw-sr-only") }, label),
            cloneElement(button, {
                id: buttonId,
                "aria-haspopup": "dialog",
                "aria-expanded": isOpen,
                "aria-labelledby": "".concat(labelId, " ").concat(buttonId),
                isOpen: isOpen,
                onClick: function () {
                    setPopoverState ? setPopoverState({ id: id, isOpen: true }) : noop;
                },
                onKeyDown: handleKeyDown,
                onMouseDown: togglePopover,
                ref: triggerRef
            })),
        React.createElement(Portal, null,
            React.createElement("div", { ref: containerRef, className: "tw-z-20" }, (shouldRender || isOpen) && (React.createElement(FocusOn, { enabled: isOpen, noIsolation: true, onClickOutside: closePopover, onEscapeKey: closePopover, scrollLock: false, shards: shards },
                React.createElement(Card, { addClassName: classNames(cardClassName, defaultStyle), role: "dialog", shadow: "md" }, children)))))));
};

export { Popover };
//# sourceMappingURL=Popover.esm.js.map
